// ============================================
// 3. COMPONENT - create-event.component.ts (create + edit)
// ============================================

import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  OnInit,
  HostListener,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ATTENDANCE_LEVELS,
  CreateEventDto,
  EVENT_TYPES,
  EventLocation,
} from 'src/app/models/event.model';
import { EventService } from 'src/app/services/event.service';
import { AuthService } from 'src/app/services/auth.service';
import { LocationService } from 'src/app/services/location.service';
import { RouteResult, ValidatedAddress } from 'src/app/models/location.models';
import { LocationPickerComponent } from 'src/app/shared/components/location-picker/location-picker.component';
import {
  EventConflictService,
  ConflictInfo,
} from 'src/app/services/event-conflict.service';
import { EventRefreshService } from 'src/app/services/event-refresh.service';
import { EventConflictWarningComponent } from 'src/app/shared/components/event-conflict-warning/event-conflict-warning.component';
import { LogService } from '../../../services/log.service';
import { AnalyticsService } from '../../../services/analytics.service';

@Component({
  selector: 'app-event-create',
  templateUrl: './create-event.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LocationPickerComponent,
    EventConflictWarningComponent,
  ],
  styleUrls: ['./create-event.component.css'],
})
export class CreateEventComponent implements OnInit {
  private log = inject(LogService);
  private analytics = inject(AnalyticsService);
  private cdr = inject(ChangeDetectorRef);
  eventForm!: FormGroup;
  eventTypes = EVENT_TYPES;
  attendanceLevels = ATTENDANCE_LEVELS;
  isLoading = false;
  isSaving = false;
  errorMessage = '';
  limitReached = false;
  calculatedDuration = '';
  minDate: string;
  maxDate: string;
  timeSlots: string[] = [];
  startTimeOpen = false;
  endTimeOpen = false;

  // Location (LocationPicker)
  userPreselectedAddress: ValidatedAddress | null = null;
  /** Adresse événement pré-sélectionnée (mode duplication) — passée au LocationPicker [preselectedAddress] */
  duplicatedEventAddress: ValidatedAddress | null = null;
  userActionRadius = 30;
  selectedLocation: EventLocation | null = null;
  /** Données de trajet OSRM reçues du LocationPicker (sauvegardées en DB) */
  selectedRouteData: RouteResult | null = null;

  /** ID de l'événement si mode édition */
  editEventId: string | null = null;
  get isEditMode(): boolean {
    return !!this.editEventId;
  }

  /** Conflict warning state */
  conflictWarning: ConflictInfo[] = [];
  private pendingFormData: any = null;

  constructor(
    private fb: FormBuilder,
    private eventService: EventService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private locationService: LocationService,
    private eventConflictService: EventConflictService,
    private eventRefreshService: EventRefreshService,
  ) {
    this.minDate = new Date().toISOString().split('T')[0];
    // No maximum date limit: merchants can schedule events arbitrarily in the future
    this.maxDate = '';

    // Generate time slots every 30 minutes (00:00 .. 23:30)
    this.timeSlots = this.generateTimeSlots(30);
  }

  private generateTimeSlots(stepMinutes: number): string[] {
    const slots: string[] = [];
    for (let minutes = 0; minutes < 24 * 60; minutes += stepMinutes) {
      const h = Math.floor(minutes / 60)
        .toString()
        .padStart(2, '0');
      const m = (minutes % 60).toString().padStart(2, '0');
      slots.push(`${h}:${m}`);
    }
    return slots;
  }

  ngOnInit(): void {
    this.initForm();
    this.setupDurationCalculation();
    this.loadUserLocation();

    // Mode édition si :id présent dans la route
    this.editEventId = this.route.snapshot.paramMap.get('id');
    if (this.editEventId) {
      this.loadEventForEdit(this.editEventId);
    } else {
      // Mode création : pré-remplissage depuis sessionStorage (duplication) ou queryParams (opportunité)
      this.loadDuplicationData();
    }
  }

  openStartTime(event: Event): void {
    event.stopPropagation();
    this.startTimeOpen = !this.startTimeOpen;
    if (this.startTimeOpen) {
      setTimeout(() => {
        document
          .getElementById('start-09:00')
          ?.scrollIntoView({ block: 'center' });
      }, 0);
    }
  }

  openEndTime(event: Event): void {
    event.stopPropagation();
    this.endTimeOpen = !this.endTimeOpen;
    if (this.endTimeOpen) {
      setTimeout(() => {
        document
          .getElementById('end-17:00')
          ?.scrollIntoView({ block: 'center' });
      }, 0);
    }
  }

  selectTime(controlName: string, slot: string): void {
    this.eventForm.get(controlName)?.setValue(slot);
    this.calculateDuration();
    this.startTimeOpen = false;
    this.endTimeOpen = false;
  }

  // ── Location autocomplete (Nominatim/OSM) ──────────────────────

  @HostListener('document:click', ['$event'])
  onDocumentClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    if (!target.closest('.time-dropdown')) {
      this.startTimeOpen = false;
      this.endTimeOpen = false;
    }
  }

  // ── User location loading ──────────────────────────────────────

  private loadUserLocation(): void {
    const user = this.authService.getCurrentUser();
    const userId = (user as any)?._id || user?.id;
    if (!userId) {
      this.log.warn(
        '[CreateEvent] user.id manquant, impossible de charger le profil location',
      );
      return;
    }
    this.locationService.getLocationProfile(userId, true).subscribe({
      next: (profile) => {
        if (profile.hasLocation && profile.coordinates) {
          this.userActionRadius = profile.actionRadius ?? 30;
          this.userPreselectedAddress = {
            valid: true,
            address: profile.address || profile.city || '',
            formatted: profile.address || profile.city || '',
            city: profile.city || '',
            postalCode: profile.postalCode || '',
            country: profile.country || '',
            coordinates: profile.coordinates,
            confidence: 1,
          };
          this.log.debug(
            '[CreateEvent] userPreselectedAddress set:',
            this.userPreselectedAddress.city,
            'radius:',
            this.userActionRadius,
          );
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.log.error('[CreateEvent] Erreur getLocationProfile:', err);
      },
    });
  }

  /**
   * Charge les données de duplication depuis sessionStorage.
   * Les données sont supprimées après lecture (one-shot).
   */
  private loadDuplicationData(): void {
    const raw = sessionStorage.getItem('duplicateEvent');
    if (!raw) return;
    sessionStorage.removeItem('duplicateEvent');
    try {
      const data = JSON.parse(raw);
      this.eventForm.patchValue({
        name: data.name || '',
        eventType: data.eventType || '',
        startTime: data.startTime || '',
        endTime: data.endTime || '',
        expectedAttendance: data.expectedAttendance || 'unknown',
        notes: data.notes || '',
      });
      this.calculateDuration();

      // Pré-remplir la location (adresse événement)
      if (data.location?.coordinates) {
        const coords = data.location.coordinates;
        const address = data.location.address || '';
        const city = data.location.city || '';
        const loc: EventLocation = { coordinates: coords, address, city };
        this.selectedLocation = loc;
        this.eventForm.get('location')?.setValue(loc);
        this.eventForm.get('location')?.updateValueAndValidity();
        // Passer au LocationPicker comme preselectedAddress (pas baseAddress)
        this.duplicatedEventAddress = {
          valid: true,
          address,
          formatted: address,
          city,
          postalCode: '',
          country: '',
          coordinates: coords,
          confidence: 1,
        };
      }
    } catch {
      // ignore invalid JSON
    }
  }

  onAddressSelected(addr: ValidatedAddress): void {
    // Reset route data — will be set by onRouteUpdated once OSRM responds
    this.selectedRouteData = null;
    const loc: EventLocation = {
      address: addr.formatted || addr.address,
      city: addr.city,
      coordinates: { lat: addr.coordinates.lat, lng: addr.coordinates.lng },
    };
    this.selectedLocation = loc;
    this.eventForm.get('location')?.setValue(loc);
    this.eventForm.get('location')?.updateValueAndValidity();
  }

  /** Reçoit les données de trajet OSRM du LocationPicker et les fusionne dans la location */
  onRouteUpdated(route: RouteResult | null): void {
    this.selectedRouteData = route;
    if (this.selectedLocation) {
      const loc: EventLocation = {
        ...this.selectedLocation,
        routeData: route
          ? {
              distanceKm: route.distanceKm,
              durationMin: route.durationMin,
              geometry: route.geometry as any,
            }
          : undefined,
      };
      this.selectedLocation = loc;
      this.eventForm.get('location')?.setValue(loc);
    }
  }

  private loadEventForEdit(id: string): void {
    this.isLoading = true;
    this.eventService.getEvent(id).subscribe({
      next: (event) => {
        // En mode édition, pas de contrainte de date min/max
        this.minDate = '';
        this.maxDate = '';
        this.eventForm.patchValue({
          name: event.name,
          eventDate: new Date(event.eventDate).toISOString().split('T')[0],
          startTime: event.startTime,
          endTime: event.endTime,
          eventType: event.eventType,
          expectedAttendance: event.expectedAttendance || 'unknown',
          notes: event.notes || '',
        });
        // Reconstruct location object from string or existing EventLocation object
        if (event.location) {
          const rawLoc = event.location;
          const loc: EventLocation =
            typeof rawLoc === 'object'
              ? (rawLoc as EventLocation)
              : {
                  address: rawLoc as string,
                  city: rawLoc as string,
                  coordinates: { lat: event.lat ?? 0, lng: event.lng ?? 0 },
                };
          this.selectedLocation = loc;
          this.eventForm.get('location')?.setValue(loc);
          // Pre-center the map on the event location
          this.userPreselectedAddress = {
            valid: true,
            address: loc.address,
            formatted: loc.address,
            city: loc.city,
            postalCode: '',
            country: '',
            coordinates: loc.coordinates,
            confidence: 1,
          };
        }
        this.calculateDuration();
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.errorMessage = "Impossible de charger l'événement";
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  initForm(): void {
    this.eventForm = this.fb.group(
      {
        name: [
          '',
          [
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(100),
          ],
        ],
        eventDate: ['', [Validators.required, this.pastDateValidator]],
        startTime: ['', Validators.required],
        endTime: ['', Validators.required],
        location: [null, Validators.required],
        eventType: ['', Validators.required],
        expectedAttendance: ['unknown'],
        notes: ['', Validators.maxLength(500)],
        recurrence: ['none'],
      },
      { validators: this.timeValidator },
    );
  }

  pastDateValidator(control: AbstractControl): ValidationErrors | null {
    const val = control.value;
    if (!val) return null;
    const sel = new Date(val);
    sel.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (sel < today) {
      return { pastDate: 'La date sélectionnée est dans le passé' };
    }
    return null;
  }

  setupDurationCalculation(): void {
    this.eventForm.get('startTime')?.valueChanges.subscribe(() => {
      this.calculateDuration();
      this.cdr.markForCheck();
    });
    this.eventForm.get('endTime')?.valueChanges.subscribe(() => {
      this.calculateDuration();
      this.cdr.markForCheck();
    });
  }

  calculateDuration(): void {
    const start = this.eventForm.get('startTime')?.value;
    const end = this.eventForm.get('endTime')?.value;

    if (start && end) {
      const [startHour, startMin] = start.split(':').map(Number);
      const [endHour, endMin] = end.split(':').map(Number);

      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      if (endMinutes > startMinutes) {
        const durationMinutes = endMinutes - startMinutes;
        const hours = Math.floor(durationMinutes / 60);
        const minutes = durationMinutes % 60;

        if (minutes === 0) {
          this.calculatedDuration = `Durée : ${hours} heure${
            hours > 1 ? 's' : ''
          }`;
        } else {
          this.calculatedDuration = `Durée : ${hours}h${minutes
            .toString()
            .padStart(2, '0')}`;
        }
      } else {
        this.calculatedDuration = '';
      }
    }
  }

  timeValidator(group: FormGroup): { [key: string]: any } | null {
    const start = group.get('startTime')?.value;
    const end = group.get('endTime')?.value;

    if (!start || !end) return null;

    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);

    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const duration = endMinutes - startMinutes;

    if (duration <= 0) {
      return { invalidTime: "L'heure de fin doit être après l'heure de début" };
    }

    if (duration < 60) {
      return { minDuration: 'La durée minimum est de 1 heure' };
    }

    if (duration > 1440) {
      return { maxDuration: 'La durée maximum est de 24 heures' };
    }

    return null;
  }

  getEventTypeDetails(value: string) {
    return this.eventTypes.find((t) => t.value === value);
  }

  onSubmit(): void {
    if (this.eventForm.valid) {
      const formData: CreateEventDto = this.eventForm.value;

      if (this.isEditMode) {
        // Mode édition : pas de vérification de conflit
        this.doSave(formData);
      } else {
        // Mode création : vérifier les conflits d'abord
        this.eventService.getEvents().subscribe({
          next: (events) => {
            const candidate = {
              id: '',
              name: formData.name,
              eventDate: formData.eventDate,
              startTime: (formData as any).startTime ?? null,
              endTime: (formData as any).endTime ?? null,
            };
            const conflicts = this.eventConflictService.findConflicts(
              events,
              candidate,
            );
            if (conflicts.length > 0) {
              this.conflictWarning = conflicts;
              this.pendingFormData = formData;
            } else {
              this.doSave(formData);
            }
            this.cdr.markForCheck();
          },
          error: () => this.doSave(formData), // en cas d'erreur API → créer quand même
        });
      }
    } else {
      Object.keys(this.eventForm.controls).forEach((key) => {
        this.eventForm.get(key)?.markAsTouched();
      });
    }
  }

  onConflictConfirm(): void {
    this.conflictWarning = [];
    if (this.pendingFormData) {
      this.doSave(this.pendingFormData);
      this.pendingFormData = null;
    }
  }

  onConflictCancel(): void {
    this.conflictWarning = [];
    this.pendingFormData = null;
  }

  private doSave(formData: CreateEventDto): void {
    this.isSaving = true;
    this.errorMessage = '';
    this.limitReached = false;

    if (this.isEditMode) {
      this.eventService.updateEvent(this.editEventId!, formData).subscribe({
        next: () => {
          this.isSaving = false;
          this.analytics.trackEventValide();
          this.router.navigate(['/events', this.editEventId]);
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isSaving = false;
          this.errorMessage =
            error.error?.message ||
            "Une erreur est survenue lors de la modification de l'événement";
          this.cdr.markForCheck();
        },
      });
    } else {
      this.eventService.createEvent(formData).subscribe({
        next: () => {
          this.isSaving = false;
          this.analytics.trackEventCree(formData.eventType);
          // trigger() auto via EventService.tap — pas besoin de l'appeler manuellement
          this.router.navigate(['/events']);
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isSaving = false;
          this.limitReached = error.error?.code === 'EVENT_LIMIT_REACHED';
          this.errorMessage =
            error.error?.message ||
            "Une erreur est survenue lors de la création de l'événement";
          this.cdr.markForCheck();
        },
      });
    }
  }

  goToPlans(): void {
    this.router.navigate(['/subscription']);
  }

  cancel(): void {
    if (this.isEditMode) {
      this.router.navigate(['/events', this.editEventId]);
    } else {
      this.router.navigate(['/dashboard']);
    }
  }
}
